import * as React from 'react'
import ReactSelect from 'react-select'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../../core/i18n'
import { adminService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import { CheckBox, InputText, InputTextArea } from '../../../atoms'
import AdminLayoutEdit from '../_layouts/edit'

const FIELDS = {
  id: 'id',
  username: 'username',
  bio: 'bio',
  email: 'email',
  email_notification: 'email_notification',
  comment: 'comment',
  fullname: 'fullname',
  fullname_furi: 'fullname_furi',
  post_code: 'post_code',
  address: 'address',
  tel: 'tel',
  sub_email: 'sub_email',
}

const title = I18n.t('admin.edit', {
  model: I18n.t('user', { scope: 'activerecord.models' }),
})
const selectCustomStyles = {
  control: (provided, state) => ({
    ...provided,
    border: `1px solid ${constants.COLORS.divider}`,
    borderRadius: 4,
    '&:hover': { border: `1px solid ${constants.COLORS.primary}` },
  }),
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: constants.COLORS.primary,
  }),
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: constants.COLORS.white,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: constants.COLORS.white,
  }),
}

const AdminUserEdit: React.FC<{ user: any; agents: any; current_agents: any }> = props => {
  const { data } = adminService.getDataFromJson(props.user)
  const { data: agents } = adminService.getDataFromJson(props.agents)
  const { data: current_agents } = adminService.getDataFromJson(props.current_agents)
  const [agentIds, setAgentIds] = React.useState(current_agents.map(agent => agent.id))
  const currentUserType = constants.USER_TYPES.find(usertype => usertype.key === data.user_type)
  const [selectedUserType, setSelectedUserType] = React.useState(
    currentUserType
      ? {
          value: currentUserType.key,
          label: currentUserType.label,
        }
      : null
  )
  const [isAdmin, setIsAdmin] = React.useState<boolean>(data.admin)
  const [status, setStatus] = React.useState(data.status)
  const handleSubmit = React.useCallback(
    async (initialValues, values) => {
      const param = values
      if (!isAdmin && selectedUserType !== null) {
        param.user_type = selectedUserType?.value
      }
      if (data.user_type === 'guide') {
        param.status = status
      }
      param.agent_ids = agentIds
      await adminService.updateUser(param)
      // TODO 遷移先でのFlush
      location.href = '/admin/users'
    },
    [isAdmin, selectedUserType, agentIds, status]
  )
  return (
    <AdminLayoutEdit
      model="user"
      title={title}
      fields={FIELDS}
      indexLink="/admin/users"
      formItems={
        <>
          <S.Section>
            <InputText readonly={true} name="id" defaultValue={data.id} label="ID" />
            <InputText
              required={true}
              name="username"
              defaultValue={data.username}
              label={I18n.t('generic.username')}
            />
            <InputTextArea
              required={true}
              name="bio"
              defaultValue={data.bio}
              label={I18n.t('generic.bio')}
            />
            <InputText
              required={true}
              name="email"
              defaultValue={data.email}
              label={I18n.t('generic.email')}
            />
            <S.CheckBoxWrapper>
              <CheckBox
                name="email_notification"
                defaultChecked={data.email_notification}
                label={I18n.t('settings.email_notification')}
              />
            </S.CheckBoxWrapper>
          </S.Section>
          <InputText
            required={true}
            name="fullname"
            label={I18n.t('generic.fullname')}
            defaultValue={data.fullname}
          />
          <InputText
            required={false}
            name="fullname_furi"
            label={I18n.t('generic.fullname_furi')}
            defaultValue={data.fullname_furi}
          />
          <InputText
            required={false}
            name="tel"
            label={I18n.t('generic.tel')}
            defaultValue={data.tel}
          />
          <InputText
            required={false}
            name="post_code"
            label={I18n.t('generic.post_code')}
            defaultValue={data.post_code}
          />
          <InputText
            required={false}
            name="address"
            label={I18n.t('generic.address')}
            defaultValue={data.address}
          />
          <InputText
            required={false}
            name="sub_email"
            label={I18n.t('generic.sub_email')}
            defaultValue={data.sub_email}
          />
          <InputText
            required={false}
            name="comment"
            label={I18n.t('generic.comment')}
            defaultValue={data.comment}
          />
          {data.user_type === 'guide' && (
            <>
              <S.ReactSelectWrapper>
                <p className="ReactSelect_Title">UserType</p>
                <div className="ReactSelect_Main">
                  <ReactSelect
                    placeholder="Select User Type"
                    options={constants.USER_TYPES.filter(
                      value => value.key === 'guide' || value.key === 'agent'
                    ).map(value => ({
                      value: value.key,
                      label: value.label,
                    }))}
                    isMulti={false}
                    onChange={setSelectedUserType}
                    styles={selectCustomStyles}
                    defaultValue={selectedUserType}
                    menuPortalTarget={document.querySelector('body')}
                  />
                </div>
              </S.ReactSelectWrapper>
              <S.ReactSelectWrapper>
                <p className="ReactSelect_Title">
                  {I18n.t('user.status', { scope: 'activerecord.attributes' })}
                </p>
                <div className="ReactSelect_Main">
                  <ReactSelect
                    options={constants.GUIDE_STATUSES.map(value => ({
                      value,
                      label: I18n.t(`enums.user.status.${value}`),
                    }))}
                    isMulti={false}
                    isClearable={false}
                    onChange={option => setStatus(option.value)}
                    styles={selectCustomStyles}
                    value={{ value: status, label: I18n.t(`enums.user.status.${status}`) }}
                    menuPortalTarget={document.querySelector('body')}
                  />
                </div>
              </S.ReactSelectWrapper>
              <S.Note>{I18n.t('admin.user_edit_note')}</S.Note>
              {selectedUserType?.value === 'guide' && (
                <S.ReactSelectWrapper>
                  <p className="ReactSelect_Title">{I18n.t('generic.agent_relations')}</p>
                  <div className="ReactSelect_Main">
                    <ReactSelect
                      placeholder={I18n.t('generic.agent_relations')}
                      options={agents.map(agent => ({
                        value: String(agent.id),
                        label: agent.username,
                      }))}
                      isMulti={true}
                      onChange={items => setAgentIds(items?.map(item => Number(item.value)) || [])}
                      styles={selectCustomStyles}
                      defaultValue={current_agents.map(agent => ({
                        value: String(agent.id),
                        label: agent.username,
                      }))}
                      menuPortalTarget={document.querySelector('body')}
                    />
                  </div>
                </S.ReactSelectWrapper>
              )}
            </>
          )}
        </>
      }
      handleSubmit={handleSubmit}
    />
  )
}
const S: { [key: string]: AnyStyledComponent } = {}
S.Section = styled.div`
  margin-bottom: 24px;
`
S.ReactSelectWrapper = styled.div`
  display: flex;
  margin-top: 24px;
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
  .ReactSelect_Title {
    min-width: 240px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    margin-top: 8px;

    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      margin-bottom: 12px;
    }
  }
  .ReactSelect_Main {
    flex: 1;
    font-size: 15px;
  }
`
S.Note = styled.div`
  font-size: 14px;
  color: ${constants.COLORS.cautionText};
  margin-left: 240px;
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    margin-left: 0;
  }
`
S.CheckBoxWrapper = styled.div`
  margin: 24px 0;
`
export default AdminUserEdit
